/**
 * Durum izleme, CM içe aktarma, arıza ve PMS uyarı ekranlarının metinleri (M7c Task 21a–21c; ADR
 * 0029 K18–K25). Hata kodları domain'deki `PMS_CONDITION_ERROR_CODES` ve `CM_ROW_CODES`
 * listelerini karşılar [NR496 4.1.5] (`locales.test.ts`).
 */

export const pmsConditionErrorsEn = {
  LIMITS_REQUIRED: 'Enter at least one limit.',
  INVALID_LIMITS:
    'The limits are not in order: the danger limit must be outside the alert band and each lower limit below each upper limit.',
  PROVIDER_REQUIRED: 'Name the condition monitoring provider of the limit.',
  AUTO_DEFECT_OFF_REASON_REQUIRED: 'Write why a FAIL reading does not open a defect.',
  BASELINE_DATE_REQUIRED: 'Enter the date the baseline was taken.',
  UNIT_REQUIRED: 'Enter the unit.',
  INVALID_POINT: 'Some fields of the measurement point are invalid.',
  DUPLICATE_POINT: 'This item already has a measurement point with this name.',
  POINT_DELETED: 'The measurement point has been removed.',
  POINT_IN_USE: 'A job is triggered by this point. Remove it from the job first.',
  POINT_EQUIPMENT_FIXED: 'A measurement point cannot be moved to another item.',
  POINT_MISMATCH: 'The measurement point belongs to another item.',
  UNIT_LOCKED: 'The point has readings: its unit cannot change.',
  UNIT_MISMATCH: 'The unit does not match the measurement point.',
  LIMIT_CHANGE_REASON_REQUIRED: 'Write why the limits change.',
  BASELINE_CHANGE_REASON_REQUIRED: 'Write why the baseline changes.',
  BASELINE_ORIGINAL_FIXED: 'The first baseline is kept and cannot change.',
  BASELINE_ORIGINAL_MISMATCH: 'The first baseline does not match. Reload and try again.',
  INVALID_VALUE: 'Enter a number.',
  LOCAL_DATE_MISMATCH: 'The ship date of the reading does not match. Reload and try again.',
  LIMITS_MISMATCH: 'The limits changed meanwhile. Reload and try again.',
  RESULT_MISMATCH: 'The result does not match the limits. Reload and try again.',
  INVALID_MEASUREMENT: 'The reading is not valid.',
  IMPORT_BATCH_REQUIRED: 'The reading must belong to an import.',
  PARAMETER_REQUIRED: 'Enter a value for every condition parameter of the job.',
  DELETE_REASON_REQUIRED: 'Write why it is removed.',
  ALREADY_IMPORTED: 'This file has already been imported on this vessel.',
  MISSING_COLUMN: 'A column of the template is missing.',
  EMPTY_FILE: 'The file has no rows.',
  TOO_MANY_ROWS: 'The file has more than 20,000 rows.',
  IMPORT_HAS_ERRORS: 'The file has row errors. Nothing was imported.',
  VALUES_ONLY_ON_BOARD: 'Readings are entered on board; the office imports limits only.',
  INVALID_FILE: 'The file cannot be read.',
  UPLOAD_PURPOSE_MISMATCH: 'The uploaded file was not uploaded for this import.',
  INVALID_CM_BATCH: 'The import is not valid.',
  INVALID_DEFECT: 'Some fields of the defect are invalid.',
  DESCRIPTION_REQUIRED: 'Describe the defect.',
  MEASUREMENT_REQUIRED: 'A defect opened by a reading must refer to it.',
  NOT_IN_VERIFICATION: 'Move the defect to Verification before closing it.',
  ROOT_CAUSE_REQUIRED: 'Enter the root cause.',
  CORRECTIVE_ACTION_REQUIRED: 'Enter the corrective action.',
  REPAIR_NOT_REPORTED: 'The repair work order has not been reported yet.',
  REPAIR_REQUIRED: 'Report a repair work order or write why no repair is needed.',
  NOT_OPERATIONAL: 'A defect cannot be closed while the item is not operational.',
  CLASS_NOTIFICATION_REQUIRED:
    'Breakdown of a class item: enter the class notification reference or why it is not needed.',
  DEFECT_FIXED: 'The opening fields of a defect cannot change.',
  DEFECT_NO_MISMATCH: 'The defect number does not match. Reload and try again.',
  DEFECT_CLOSED: 'The defect is closed.',
  DEFECT_PENDING_CLOSURE: 'The defect is waiting for closure approval.',
  MEASUREMENT_MISMATCH: 'The reading belongs to another item.',
  MEASUREMENT_NOT_FAILED: 'The reading did not fail.',
  REPAIR_WORK_ORDER_MISMATCH: 'The repair work order belongs to another defect.',
  REPAIR_WORK_ORDER_EXISTS: 'The defect already has a repair work order.',
  DEFECT_MISMATCH: 'The work order belongs to another defect.',
  DEFECT_REQUIRED: 'A breakdown work order needs its breakdown.',
  RECORD_TYPE_MISMATCH: 'The record type does not match.',
  ALREADY_SUBMITTED: 'The closure is already waiting for approval.',
  UNKNOWN_EQUIPMENT: 'Unknown equipment code.',
  POINT_REQUIRED: 'Enter the point name.',
  INVALID_NUMBER: 'Enter a number (decimal point or comma, no thousands separator).',
  LIMIT_SOURCE_REQUIRED: 'Enter the limit source.',
  INVALID_LIMIT_SOURCE: 'Unknown limit source. Use oem, standard, cm_provider, class or company.',
  MEASURED_AT_REQUIRED: 'Enter when the value was measured.',
  VALUE_REQUIRED: 'Enter the value.',
  DUPLICATE_MEASUREMENT: 'The same point and time appear twice in the file.',
  CONFLICTING_THRESHOLDS: 'Rows of the same point carry different limits.',
  MEASUREMENT_EXISTS: 'This reading is already recorded; it is skipped.',
  INVALID_PMS_SETTINGS: 'Some PMS settings are out of range.',
  // DM-17 (ADR 0060)
  CAPTAIN_ACTION_REQUIRED: 'Enter the action taken by the captain (Part B).',
  PROCEDURE_REVIEW_REQUIRED: 'Answer whether the SMS procedures need an update.',
  PROCEDURE_REVIEW_INCOMPLETE: 'Name the procedure and how it can be improved.',
  RECTIFIED_DATE_REQUIRED: 'Enter the date rectified.',
  ACTIONS_OPEN: 'Complete or cancel every corrective action first.',
  SYSTEM_REQUIRED: 'Enter the system when no PMS equipment is selected.',
  EQUIPMENT_REQUIRED: 'Select the equipment: breakdowns and repair work orders need equipment.',
};

export const pmsConditionErrorsTr: typeof pmsConditionErrorsEn = {
  LIMITS_REQUIRED: 'En az bir sınır girin.',
  INVALID_LIMITS:
    'Sınırlar sıralı değil: tehlike sınırı uyarı bandının dışında, her alt sınır her üst sınırdan küçük olmalı.',
  PROVIDER_REQUIRED: 'Limitin durum izleme sağlayıcısını yazın.',
  AUTO_DEFECT_OFF_REASON_REQUIRED: 'FAIL ölçümün neden arıza açmadığını yazın.',
  BASELINE_DATE_REQUIRED: 'Baseline değerinin alındığı tarihi girin.',
  UNIT_REQUIRED: 'Birimi girin.',
  INVALID_POINT: 'Ölçüm noktasının bazı alanları geçersiz.',
  DUPLICATE_POINT: 'Bu öğede aynı adlı bir ölçüm noktası var.',
  POINT_DELETED: 'Ölçüm noktası kaldırılmış.',
  POINT_IN_USE: 'Bir iş bu noktayla tetikleniyor. Önce işten çıkarın.',
  POINT_EQUIPMENT_FIXED: 'Ölçüm noktası başka bir öğeye taşınamaz.',
  POINT_MISMATCH: 'Ölçüm noktası başka bir öğeye ait.',
  UNIT_LOCKED: 'Noktanın ölçümleri var: birimi değişemez.',
  UNIT_MISMATCH: 'Birim ölçüm noktasıyla uyuşmuyor.',
  LIMIT_CHANGE_REASON_REQUIRED: 'Limitlerin neden değiştiğini yazın.',
  BASELINE_CHANGE_REASON_REQUIRED: 'Baseline değerinin neden değiştiğini yazın.',
  BASELINE_ORIGINAL_FIXED: 'İlk baseline korunur, değişemez.',
  BASELINE_ORIGINAL_MISMATCH: 'İlk baseline uyuşmuyor. Yenileyip yeniden deneyin.',
  INVALID_VALUE: 'Sayı girin.',
  LOCAL_DATE_MISMATCH: 'Ölçümün gemi tarihi uyuşmuyor. Yenileyip yeniden deneyin.',
  LIMITS_MISMATCH: 'Limitler bu arada değişti. Yenileyip yeniden deneyin.',
  RESULT_MISMATCH: 'Sonuç limitlerle uyuşmuyor. Yenileyip yeniden deneyin.',
  INVALID_MEASUREMENT: 'Ölçüm geçerli değil.',
  IMPORT_BATCH_REQUIRED: 'Ölçüm bir içe aktarmaya bağlı olmalı.',
  PARAMETER_REQUIRED: 'İşin her durum parametresi için değer girin.',
  DELETE_REASON_REQUIRED: 'Neden kaldırıldığını yazın.',
  ALREADY_IMPORTED: 'Bu dosya bu gemide daha önce içe aktarıldı.',
  MISSING_COLUMN: 'Şablonun bir sütunu eksik.',
  EMPTY_FILE: 'Dosyada satır yok.',
  TOO_MANY_ROWS: 'Dosyada 20.000’den çok satır var.',
  IMPORT_HAS_ERRORS: 'Dosyada satır hataları var. Hiçbir şey içe aktarılmadı.',
  VALUES_ONLY_ON_BOARD: 'Ölçüm değerleri gemide girilir; ofis yalnız limitleri aktarır.',
  INVALID_FILE: 'Dosya okunamıyor.',
  UPLOAD_PURPOSE_MISMATCH: 'Yüklenen dosya bu içe aktarma için yüklenmemiş.',
  INVALID_CM_BATCH: 'İçe aktarma geçerli değil.',
  INVALID_DEFECT: 'Arızanın bazı alanları geçersiz.',
  DESCRIPTION_REQUIRED: 'Arızayı anlatın.',
  MEASUREMENT_REQUIRED: 'Ölçümün açtığı arıza o ölçüme bağlı olmalı.',
  NOT_IN_VERIFICATION: 'Kapatmadan önce arızayı Doğrulama adımına getirin.',
  ROOT_CAUSE_REQUIRED: 'Kök nedeni girin.',
  CORRECTIVE_ACTION_REQUIRED: 'Düzeltici faaliyeti girin.',
  REPAIR_NOT_REPORTED: 'Onarım iş emri henüz raporlanmadı.',
  REPAIR_REQUIRED: 'Onarım iş emrini raporlayın ya da onarım neden gerekmediğini yazın.',
  NOT_OPERATIONAL: 'Öğe çalışmaz durumdayken arıza kapanmaz.',
  CLASS_NOTIFICATION_REQUIRED:
    'Klas kalemindeki breakdown: klas bildirim referansını ya da neden gerekmediğini girin.',
  DEFECT_FIXED: 'Arızanın açılış alanları değişemez.',
  DEFECT_NO_MISMATCH: 'Arıza numarası uyuşmuyor. Yenileyip yeniden deneyin.',
  DEFECT_CLOSED: 'Arıza kapanmış.',
  DEFECT_PENDING_CLOSURE: 'Arıza kapanış onayı bekliyor.',
  MEASUREMENT_MISMATCH: 'Ölçüm başka bir öğeye ait.',
  MEASUREMENT_NOT_FAILED: 'Ölçüm FAIL değil.',
  REPAIR_WORK_ORDER_MISMATCH: 'Onarım iş emri başka bir arızaya ait.',
  REPAIR_WORK_ORDER_EXISTS: 'Arızanın zaten bir onarım iş emri var.',
  DEFECT_MISMATCH: 'İş emri başka bir arızaya ait.',
  DEFECT_REQUIRED: 'Breakdown iş emri breakdown kaydıyla açılır.',
  RECORD_TYPE_MISMATCH: 'Kayıt türü uyuşmuyor.',
  ALREADY_SUBMITTED: 'Kapanış zaten onay bekliyor.',
  UNKNOWN_EQUIPMENT: 'Bilinmeyen ekipman kodu.',
  POINT_REQUIRED: 'Nokta adını girin.',
  INVALID_NUMBER: 'Sayı girin (ondalık nokta ya da virgül, binlik ayraç yok).',
  LIMIT_SOURCE_REQUIRED: 'Limit kaynağını girin.',
  INVALID_LIMIT_SOURCE:
    'Bilinmeyen limit kaynağı. oem, standard, cm_provider, class ya da company kullanın.',
  MEASURED_AT_REQUIRED: 'Değerin ölçüldüğü zamanı girin.',
  VALUE_REQUIRED: 'Değeri girin.',
  DUPLICATE_MEASUREMENT: 'Aynı nokta ve zaman dosyada iki kez var.',
  CONFLICTING_THRESHOLDS: 'Aynı noktanın satırları farklı limit taşıyor.',
  MEASUREMENT_EXISTS: 'Bu ölçüm zaten kayıtlı; atlanır.',
  INVALID_PMS_SETTINGS: 'Bazı PMS ayarları aralık dışında.',
  // DM-17 (ADR 0060)
  CAPTAIN_ACTION_REQUIRED: 'Kaptanın aldığı önlemi girin (Bölüm B).',
  PROCEDURE_REVIEW_REQUIRED:
    'SMS prosedürlerinin güncelleme gerektirip gerektirmediğini cevaplayın.',
  PROCEDURE_REVIEW_INCOMPLETE: 'Prosedürü ve nasıl iyileştirileceğini yazın.',
  RECTIFIED_DATE_REQUIRED: 'Giderilme tarihini girin.',
  ACTIONS_OPEN: 'Önce her düzeltici faaliyeti tamamlayın ya da iptal edin.',
  SYSTEM_REQUIRED: 'PMS ekipmanı seçilmediyse sistemi yazın.',
  EQUIPMENT_REQUIRED: 'Ekipmanı seçin: breakdown ve onarım iş emri ekipman ister.',
};

export const pmsConditionEn = {
  results: { pass: 'PASS', abnormal: 'ABNORMAL', fail: 'FAIL' },
  limitSources: {
    oem: 'Maker (OEM)',
    standard: 'Recognised standard',
    cm_provider: 'CM provider',
    class: 'Class',
    company: 'Company',
  },
  measurementSources: { manual: 'Manual', cm_import: 'CM import' },
  points: {
    title: 'Measurement Points',
    none: 'No measurement points.',
    limit: 'Limit',
    danger: 'FAIL outside {{limit}}',
    last: 'Last: {{value}} {{unit}} · {{date}}',
    noReading: 'No reading yet',
    estimated: 'Estimated (trend)',
    estimatedAlert: 'Alert limit {{date}}',
    estimatedDanger: 'Danger limit {{date}}',
    estimatedHint:
      'Linear trend of the last {{count}} readings. Display only: the due date is set by the limit, not by the trend.',
    autoDefectOff: 'No automatic defect: {{reason}}',
    baseline: 'Baseline {{value}} {{unit}}',
  },
  step: {
    title: 'Measurements',
    empty: 'This work has no measurement points.',
    emptyHint: 'Measurement points are added in the Job Card.',
    hint: 'Values are recorded with Complete & Report. The result is evaluated against the limits shown.',
    parameter: 'Parameter',
    value: 'Value',
    limit: 'Limit',
    result: 'Result',
    required: 'Required',
    valueOf: 'Value of {{name}}',
    defectNotice: 'FAIL: a defect record will be opened for {{names}}.',
    noDefect: 'FAIL without automatic defect ({{names}}): open a defect if the item needs repair.',
    recorded: 'Recorded measurements',
    recordedEmpty: 'No measurements were recorded with this work order.',
    voided: 'VOID',
  },
  editor: {
    title: 'Measurement points',
    hint: 'Limits are part of the maintenance programme and come from the maker or a recognised standard.',
    saveJobFirst: 'Save the job first, then add its measurement points.',
    add: 'Add point',
    edit: 'Edit {{name}}',
    remove: 'Remove {{name}}',
    newTitle: 'New measurement point',
    editTitle: 'Measurement point · {{name}}',
    name: 'Parameter',
    unit: 'Unit',
    nominal: 'Nominal',
    alertLow: 'Alert low',
    alertHigh: 'Alert high',
    dangerLow: 'Danger low (FAIL)',
    dangerHigh: 'Danger high (FAIL)',
    lowInclusive: 'Lower limits inclusive (≥)',
    highInclusive: 'Upper limits inclusive (≤)',
    inclusiveHint: 'A range "a–b" and "±" are inclusive; a limit written with ">" or "<" is not.',
    limitSource: 'Limit source',
    provider: 'CM provider',
    baselineValue: 'Baseline',
    baselineTakenOn: 'Baseline taken on',
    baselineOriginal: 'First baseline: {{value}} {{unit}} (kept)',
    autoDefect: 'A FAIL reading opens a defect',
    autoDefectOffReason: 'Why a FAIL reading does not open a defect',
    changeReason: 'Reason for the change',
    changeReasonHint: 'Required when limits, limit source or baseline change [UR Z27 2.2.4].',
    triggers: 'Condition trigger points',
    triggersHint:
      'The job becomes due when the last reading of a selected point is ABNORMAL or FAIL.',
    removeTitle: 'Remove measurement point',
    removeBody: 'The point and its readings stay in the history. Write why it is removed.',
    removeReason: 'Reason',
  },
  history: {
    title: 'Measurement history',
    empty: 'No measurements yet.',
    date: 'Date',
    parameter: 'Parameter',
    value: 'Value',
    result: 'Result',
    source: 'Source',
    limitChanged: 'Limits changed',
    limitChangedHint: 'Evaluated with different limits than the previous reading [UR Z27 2.2.4].',
    chartLabel: '{{name}}: {{count}} readings, last {{value}} {{unit}}',
    voided: 'VOID',
  },
  cm: {
    open: 'Import CM data',
    title: 'Import condition monitoring data',
    intro:
      'One template, CSV or XLSX (first sheet), at most 10 MB. The preview writes nothing; a file with row errors is not imported.',
    introOffice:
      'Before the vessel goes live the office imports limits and baselines only. Readings are entered on board.',
    columns: 'Columns',
    choose: 'Choose file',
    chooseAgain: 'Choose another file',
    previewing: 'Reading the file…',
    tooLarge: 'The file is larger than 10 MB.',
    summary: {
      rows: 'Rows',
      created: 'New points',
      updated: 'Limit changes',
      measurements: 'Readings',
      defects: 'Defects to open',
    },
    errorsTitle: 'Row errors',
    warningsTitle: 'Notes',
    changesTitle: 'Limit and baseline changes',
    createdTitle: 'New measurement points',
    defectsTitle: 'Defects to be opened',
    row: 'Row',
    column: 'Column',
    problem: 'Problem',
    value: 'Value',
    point: 'Point',
    change: 'Change',
    equipment: 'Equipment',
    limit: 'Limit',
    measuredAt: 'Measured at',
    readingsSplit: '{{normal}} PASS · {{abnormal}} ABNORMAL · {{fail}} FAIL',
    noErrors: 'No row errors. The file can be imported.',
    apply: 'Import',
    applied:
      'Imported: {{created}} new points, {{updated}} limit changes, {{measurements}} readings.',
    appliedDefects: '{{count}} defects were opened: {{numbers}}.',
    fields: {
      unit: 'Unit',
      alertLow: 'Alert low',
      alertHigh: 'Alert high',
      dangerLow: 'Danger low',
      dangerHigh: 'Danger high',
      limitSource: 'Limit source',
      provider: 'Provider',
      baselineValue: 'Baseline',
      lowInclusive: 'Low inclusive',
      highInclusive: 'High inclusive',
    },
  },
  defects: {
    tabs: { workOrders: 'Work Orders', defects: 'Defects', breakdowns: 'Breakdowns' },
    title: 'Defects',
    titleBreakdowns: 'Breakdowns',
    search: 'Search defect…',
    searchLabel: 'Search defects',
    filterLabel: 'Status',
    filters: { all: 'All', open: 'Open', pending: 'Pending', closed: 'Closed' },
    columns: {
      no: 'Defect No',
      component: 'Component',
      description: 'Description',
      severity: 'Severity',
      status: 'Status',
      reported: 'Reported',
    },
    empty: 'No defects reported.',
    emptyBreakdowns: 'No breakdowns reported.',
    noMatch: 'No defect matches the search.',
    add: 'Report Defect',
    addBreakdown: 'Report Breakdown',
    kinds: { defect: 'Defect', breakdown: 'Breakdown' },
    severities: { minor: 'Minor', major: 'Major', critical: 'Critical' },
    states: {
      open: 'Open',
      investigating: 'Investigation',
      repair: 'Repair',
      testing: 'Test',
      verification: 'Verification',
      pending: 'Pending approval',
      returned: 'Returned',
      closed: 'Closed',
    },
    sources: {
      manual: 'Reported on board',
      measurement: 'FAIL reading',
      work_record: 'Work record',
      cm_import: 'CM import',
    },
    flowLabel: 'Defect workflow',
    form: {
      newDefect: 'Report defect',
      newBreakdown: 'Report breakdown',
      equipment: 'Equipment',
      description: 'Description',
      severity: 'Severity',
      operationalImpact: 'Operational impact',
      immediateAction: 'Immediate action',
      procedure: 'Name of procedures',
      improvement: 'How the procedure can be improved',
      reportedAt: 'Reported at (ship time)',
      breakdownHint: 'A high-priority breakdown work order is opened with it.',
      submit: 'Report',
    },
    detail: {
      openReport: 'Open Defect Report (DM-17)',
      equipment: 'Equipment',
      kind: 'Type',
      source: 'Source',
      reportedAt: 'Reported',
      reportedBy: 'Reported by',
      operationalImpact: 'Operational impact',
      yes: 'Yes',
      no: 'No',
      immediateAction: 'Immediate action',
      investigation: 'Investigation',
      rootCause: 'Root cause',
      correctiveAction: 'Corrective action',
      testResult: 'Test result',
      conditionAfter: 'Condition after repair',
      noRepairReason: 'Why no repair work order is needed',
      classNotificationRef: 'Class notification reference',
      classNotificationWaiver: 'Why the class is not notified',
      status: 'Workflow step',
      next: 'Move to',
      save: 'Save',
      saved: 'Saved.',
      repair: 'Repair work order',
      openRepair: 'Open repair work order',
      viewRepair: 'Open {{wo}}',
      noRepair: 'No repair work order.',
      submit: 'Submit for closure',
      locked: 'The closure is waiting for approval; the record cannot be changed.',
      closed: 'Closed with approval. The record is locked [NR496 4.6].',
      returned: 'The closure was returned: correct the record and submit again.',
      readOnlyOffice: 'Defects are recorded on board; the office sees them read-only.',
      classItem: 'Class item: a breakdown needs the class notification reference.',
      decide: 'Your decision',
      fromMeasurement: 'Opened by a FAIL reading.',
    },
    close: {
      title: 'Submit {{no}} for closure',
      body: 'Your submission is the crew member’s signature (DM-17). The captain approves on board, then the management in the office. What you submit is what is approved.',
      repairReported: 'Repair work order {{wo}} is reported.',
      repairNotReported: 'Repair work order {{wo}} is not reported yet. Report it first.',
      conditionHint: 'A defect cannot be closed while the item is not operational.',
      submit: 'Submit',
      procedureReview: 'Does this defect require an update to SMS procedures?',
      procedureYes: 'Yes',
      procedureNo: 'No',
    },
    decision: {
      panel: 'Breakdowns awaiting office decision',
      review: 'Review',
    },
  },
  critical: {
    only: 'Critical only',
    none: 'No critical equipment.',
    marker: 'Critical equipment',
    outOfPms: 'Out of PMS',
    reasons: {
      manual: 'Marked out of PMS',
      open_breakdown: 'Open breakdown',
      overdue_coc: 'Overdue condition of class',
    },
    mark: 'Mark Out of PMS',
    unmark: 'Return to PMS',
    markTitle: 'Mark {{name}} out of PMS',
    markBody:
      'The item stays in the register and its history is kept. It is reported as out of the planned maintenance system until it is returned [UR Z20 3.3.3].',
    unmarkTitle: 'Return {{name}} to PMS',
    unmarkBody:
      'The manual mark is removed. An open breakdown or an overdue condition of class keeps the item out of PMS until it is closed.',
    reason: 'Reason',
  },
  dashboard: {
    title: 'Planned Maintenance',
    tiles: {
      today: 'Today',
      overdue: 'Overdue',
      due7: 'Due next 7 days',
      due30: 'Due next 30 days',
      criticalOverdue: 'Critical overdue',
      openWorkOrders: 'Open work orders',
      openDefects: 'Open defects',
      breakdowns: 'Breakdowns',
    },
    todaysJobs: "Today's Jobs",
    noJobs: 'No job is due today.',
    job: 'Job',
    due: 'Due',
    status: 'Status',
    viewPlan: 'View plan',
    fleetTitle: 'PMS Compliance',
    vessel: 'Vessel',
    compliance: 'PMS Compliance',
    overdue: 'Overdue',
    criticalOverdue: 'Critical Overdue',
    openDefects: 'Open defects',
    breakdowns: 'Breakdowns',
    dataAsOf: 'Data as of',
    complianceHint:
      'Share of jobs due in the last 30 days that were not left overdue (BIMCO PI065). Approved postponements are not overdue.',
    noData: 'No PMS jobs',
    setup: 'Set-up',
    empty: 'No vessel has PMS data yet.',
    open: 'Open the equipment list of {{name}}',
  },
  bell: {
    title: 'Critical jobs overdue',
    item: '{{code}} · {{days}} days overdue',
    itemNoDays: '{{code}} · overdue',
    workOrderOverdue: 'Work order overdue',
    workOrderOverdueItem: '{{wo}} · {{days}} days past target date',
    cmReportPending: 'Condition monitoring report awaited',
    cmReportPendingItem: '{{wo}} · report from the service supplier',
  },
  settings: {
    title: 'Planned Maintenance (PMS)',
    intro:
      'Thresholds for warnings and reports. Compliance rules (no future dates, no lower running hours, at most 24 hours a day, an explicit approval step) are always enforced and are not settings [NR496 1.4.2].',
    dueSoonDays: 'Due soon window',
    dueSoonHint: 'A job shows "Due Soon" this many days before it is due.',
    lateEntryDays: 'Late entry threshold',
    lateEntryHint:
      'Records entered this many days after the work are listed in the Late Entries report.',
    certificateExpiringDays: 'Certificate "Expiring" threshold',
    certificateHint:
      'A vessel certificate shows "Expiring" this many days before expiry. Reminders go at 90, 60 and 30 days.',
    days: 'days',
    save: 'Save',
    saved: 'Saved.',
    readOnly: 'Company settings are changed in the office.',
    structure: 'Default equipment structure',
    structureHint:
      'Loads the standard systems (PMS document §1, §17) on a vessel that has no equipment yet.',
    structureVessel: 'Vessel',
    structureNone: 'Every vessel already has equipment, or no vessel is being set up.',
    structureLoaded: '{{count}} items were added.',
    load: 'Load default structure',
  },
};

export type PmsConditionResources = typeof pmsConditionEn;

export const pmsConditionTr: PmsConditionResources = {
  results: { pass: 'PASS', abnormal: 'ABNORMAL', fail: 'FAIL' },
  limitSources: {
    oem: 'Üretici (OEM)',
    standard: 'Tanınmış standart',
    cm_provider: 'CM sağlayıcısı',
    class: 'Klas',
    company: 'Şirket',
  },
  measurementSources: { manual: 'Elle', cm_import: 'CM içe aktarma' },
  points: {
    title: 'Ölçüm Noktaları',
    none: 'Ölçüm noktası yok.',
    limit: 'Limit',
    danger: 'FAIL: {{limit}} dışı',
    last: 'Son: {{value}} {{unit}} · {{date}}',
    noReading: 'Henüz ölçüm yok',
    estimated: 'Tahmini (eğilim)',
    estimatedAlert: 'Uyarı sınırı {{date}}',
    estimatedDanger: 'Tehlike sınırı {{date}}',
    estimatedHint:
      'Son {{count}} ölçümün doğrusal eğilimi. Yalnız gösterim: vadeyi eğilim değil, limit belirler.',
    autoDefectOff: 'Otomatik arıza yok: {{reason}}',
    baseline: 'Baseline {{value}} {{unit}}',
  },
  step: {
    title: 'Ölçümler',
    empty: 'Bu işin ölçüm noktası yok.',
    emptyHint: 'Ölçüm noktaları Job Card’da eklenir.',
    hint: 'Değerler Complete & Report ile kaydedilir. Sonuç gösterilen limitlere göre değerlendirilir.',
    parameter: 'Parametre',
    value: 'Değer',
    limit: 'Limit',
    result: 'Sonuç',
    required: 'Zorunlu',
    valueOf: '{{name}} değeri',
    defectNotice: 'FAIL: {{names}} için bir arıza kaydı açılacak.',
    noDefect: 'Otomatik arızası kapalı FAIL ({{names}}): öğe onarım gerektiriyorsa arıza açın.',
    recorded: 'Kaydedilen ölçümler',
    recordedEmpty: 'Bu iş emriyle ölçüm kaydedilmedi.',
    voided: 'İPTAL',
  },
  editor: {
    title: 'Ölçüm noktaları',
    hint: 'Limitler bakım programının parçasıdır; üreticiden ya da tanınmış bir standarttan gelir.',
    saveJobFirst: 'Önce işi kaydedin, sonra ölçüm noktalarını ekleyin.',
    add: 'Nokta ekle',
    edit: '{{name}} düzenle',
    remove: '{{name}} kaldır',
    newTitle: 'Yeni ölçüm noktası',
    editTitle: 'Ölçüm noktası · {{name}}',
    name: 'Parametre',
    unit: 'Birim',
    nominal: 'Nominal',
    alertLow: 'Uyarı alt',
    alertHigh: 'Uyarı üst',
    dangerLow: 'Tehlike alt (FAIL)',
    dangerHigh: 'Tehlike üst (FAIL)',
    lowInclusive: 'Alt sınırlar dahil (≥)',
    highInclusive: 'Üst sınırlar dahil (≤)',
    inclusiveHint: '"a–b" aralığı ve "±" dahildir; ">" ya da "<" ile yazılan sınır dahil değildir.',
    limitSource: 'Limit kaynağı',
    provider: 'CM sağlayıcısı',
    baselineValue: 'Baseline',
    baselineTakenOn: 'Baseline tarihi',
    baselineOriginal: 'İlk baseline: {{value}} {{unit}} (korunur)',
    autoDefect: 'FAIL ölçüm arıza açar',
    autoDefectOffReason: 'FAIL ölçümün neden arıza açmadığı',
    changeReason: 'Değişikliğin gerekçesi',
    changeReasonHint: 'Limit, limit kaynağı ya da baseline değişince zorunlu [UR Z27 2.2.4].',
    triggers: 'Durum tetikleyici noktalar',
    triggersHint: 'Seçilen noktanın son ölçümü ABNORMAL ya da FAIL olunca iş vadeye gelir.',
    removeTitle: 'Ölçüm noktasını kaldır',
    removeBody: 'Nokta ve ölçümleri geçmişte kalır. Neden kaldırıldığını yazın.',
    removeReason: 'Gerekçe',
  },
  history: {
    title: 'Ölçüm geçmişi',
    empty: 'Henüz ölçüm yok.',
    date: 'Tarih',
    parameter: 'Parametre',
    value: 'Değer',
    result: 'Sonuç',
    source: 'Kaynak',
    limitChanged: 'Limit değişti',
    limitChangedHint: 'Önceki ölçümden farklı limitlerle değerlendirildi [UR Z27 2.2.4].',
    chartLabel: '{{name}}: {{count}} ölçüm, son {{value}} {{unit}}',
    voided: 'İPTAL',
  },
  cm: {
    open: 'CM verisi aktar',
    title: 'Durum izleme verisini içe aktar',
    intro:
      'Tek şablon, CSV ya da XLSX (ilk sayfa), en çok 10 MB. Önizleme hiçbir şey yazmaz; satır hatası olan dosya aktarılmaz.',
    introOffice:
      'Gemi etkinleşmeden önce ofis yalnız limitleri ve baseline değerlerini aktarır. Ölçümler gemide girilir.',
    columns: 'Sütunlar',
    choose: 'Dosya seç',
    chooseAgain: 'Başka dosya seç',
    previewing: 'Dosya okunuyor…',
    tooLarge: 'Dosya 10 MB’tan büyük.',
    summary: {
      rows: 'Satır',
      created: 'Yeni nokta',
      updated: 'Limit değişikliği',
      measurements: 'Ölçüm',
      defects: 'Açılacak arıza',
    },
    errorsTitle: 'Satır hataları',
    warningsTitle: 'Notlar',
    changesTitle: 'Limit ve baseline değişiklikleri',
    createdTitle: 'Yeni ölçüm noktaları',
    defectsTitle: 'Açılacak arızalar',
    row: 'Satır',
    column: 'Sütun',
    problem: 'Sorun',
    value: 'Değer',
    point: 'Nokta',
    change: 'Değişiklik',
    equipment: 'Ekipman',
    limit: 'Limit',
    measuredAt: 'Ölçüm zamanı',
    readingsSplit: '{{normal}} PASS · {{abnormal}} ABNORMAL · {{fail}} FAIL',
    noErrors: 'Satır hatası yok. Dosya aktarılabilir.',
    apply: 'İçe aktar',
    applied:
      'Aktarıldı: {{created}} yeni nokta, {{updated}} limit değişikliği, {{measurements}} ölçüm.',
    appliedDefects: '{{count}} arıza açıldı: {{numbers}}.',
    fields: {
      unit: 'Birim',
      alertLow: 'Uyarı alt',
      alertHigh: 'Uyarı üst',
      dangerLow: 'Tehlike alt',
      dangerHigh: 'Tehlike üst',
      limitSource: 'Limit kaynağı',
      provider: 'Sağlayıcı',
      baselineValue: 'Baseline',
      lowInclusive: 'Alt dahil',
      highInclusive: 'Üst dahil',
    },
  },
  defects: {
    tabs: { workOrders: 'İş Emirleri', defects: 'Arızalar', breakdowns: 'Breakdown’lar' },
    title: 'Arızalar',
    titleBreakdowns: 'Breakdown’lar',
    search: 'Arıza ara…',
    searchLabel: 'Arızalarda ara',
    filterLabel: 'Durum',
    filters: { all: 'Tümü', open: 'Açık', pending: 'Onayda', closed: 'Kapanmış' },
    columns: {
      no: 'Arıza No',
      component: 'Bileşen',
      description: 'Açıklama',
      severity: 'Ciddiyet',
      status: 'Durum',
      reported: 'Bildirildi',
    },
    empty: 'Bildirilmiş arıza yok.',
    emptyBreakdowns: 'Bildirilmiş breakdown yok.',
    noMatch: 'Aramayla eşleşen arıza yok.',
    add: 'Arıza Bildir',
    addBreakdown: 'Breakdown Bildir',
    kinds: { defect: 'Arıza', breakdown: 'Breakdown' },
    severities: { minor: 'Küçük', major: 'Büyük', critical: 'Kritik' },
    states: {
      open: 'Açık',
      investigating: 'İnceleme',
      repair: 'Onarım',
      testing: 'Test',
      verification: 'Doğrulama',
      pending: 'Onay bekliyor',
      returned: 'İade edildi',
      closed: 'Kapandı',
    },
    sources: {
      manual: 'Gemide bildirildi',
      measurement: 'FAIL ölçüm',
      work_record: 'İş kaydı',
      cm_import: 'CM içe aktarma',
    },
    flowLabel: 'Arıza iş akışı',
    form: {
      newDefect: 'Arıza bildir',
      newBreakdown: 'Breakdown bildir',
      equipment: 'Ekipman',
      description: 'Açıklama',
      severity: 'Ciddiyet',
      operationalImpact: 'Operasyonel etki',
      immediateAction: 'İlk müdahale',
      procedure: 'Prosedürlerin adı',
      improvement: 'Prosedür nasıl iyileştirilebilir',
      reportedAt: 'Bildirim zamanı (gemi saati)',
      breakdownHint: 'Birlikte yüksek öncelikli bir breakdown iş emri açılır.',
      submit: 'Bildir',
    },
    detail: {
      openReport: 'Arıza Bildirimini Aç (DM-17)',
      equipment: 'Ekipman',
      kind: 'Tür',
      source: 'Kaynak',
      reportedAt: 'Bildirildi',
      reportedBy: 'Bildiren',
      operationalImpact: 'Operasyonel etki',
      yes: 'Evet',
      no: 'Hayır',
      immediateAction: 'İlk müdahale',
      investigation: 'İnceleme',
      rootCause: 'Kök neden',
      correctiveAction: 'Düzeltici faaliyet',
      testResult: 'Test sonucu',
      conditionAfter: 'Onarım sonrası durum',
      noRepairReason: 'Onarım iş emrinin neden gerekmediği',
      classNotificationRef: 'Klas bildirim referansı',
      classNotificationWaiver: 'Klasa neden bildirilmediği',
      status: 'İş akışı adımı',
      next: 'Sonraki adım',
      save: 'Kaydet',
      saved: 'Kaydedildi.',
      repair: 'Onarım iş emri',
      openRepair: 'Onarım iş emri aç',
      viewRepair: '{{wo}} aç',
      noRepair: 'Onarım iş emri yok.',
      submit: 'Kapanışa gönder',
      locked: 'Kapanış onay bekliyor; kayıt değiştirilemez.',
      closed: 'Onayla kapandı. Kayıt kilitli [NR496 4.6].',
      returned: 'Kapanış iade edildi: kaydı düzeltip yeniden gönderin.',
      readOnlyOffice: 'Arızalar gemide kaydedilir; ofis salt okunur görür.',
      classItem: 'Klas kalemi: breakdown klas bildirim referansı ister.',
      decide: 'Kararınız',
      fromMeasurement: 'FAIL ölçüm açtı.',
    },
    close: {
      title: '{{no}} kapanışa gönder',
      body: 'Gönderiminiz mürettebatın imzasıdır (DM-17). Kapanışı gemide kaptan, ardından ofiste yönetim onaylar. Gönderdiğiniz içerik onaylanan içeriktir.',
      repairReported: 'Onarım iş emri {{wo}} raporlandı.',
      repairNotReported: 'Onarım iş emri {{wo}} henüz raporlanmadı. Önce raporlayın.',
      conditionHint: 'Öğe çalışmaz durumdayken arıza kapanmaz.',
      submit: 'Gönder',
      procedureReview: 'Bu arıza SMS prosedürlerinde güncelleme gerektiriyor mu?',
      procedureYes: 'Evet',
      procedureNo: 'Hayır',
    },
    decision: {
      panel: 'Ofis kararı bekleyen breakdown’lar',
      review: 'İncele',
    },
  },
  critical: {
    only: 'Yalnız kritik',
    none: 'Kritik ekipman yok.',
    marker: 'Kritik ekipman',
    outOfPms: 'PMS dışı',
    reasons: {
      manual: 'PMS dışı işaretli',
      open_breakdown: 'Açık breakdown',
      overdue_coc: 'Vadesi geçmiş klas şartı (CoC)',
    },
    mark: 'PMS Dışı İşaretle',
    unmark: 'PMS’e Geri Al',
    markTitle: '{{name}} PMS dışı işaretle',
    markBody:
      'Öğe kayıtta kalır, geçmişi korunur. Geri alınana kadar planlı bakım sistemi dışında raporlanır [UR Z20 3.3.3].',
    unmarkTitle: '{{name}} PMS’e geri al',
    unmarkBody:
      'Elle işaret kalkar. Açık breakdown ya da vadesi geçmiş klas şartı, kapanana kadar öğeyi PMS dışında tutar.',
    reason: 'Gerekçe',
  },
  dashboard: {
    title: 'Planlı Bakım',
    tiles: {
      today: 'Bugün',
      overdue: 'Gecikmiş',
      due7: 'Önümüzdeki 7 gün',
      due30: 'Önümüzdeki 30 gün',
      criticalOverdue: 'Kritik gecikme',
      openWorkOrders: 'Açık iş emri',
      openDefects: 'Açık arıza',
      breakdowns: 'Breakdown',
    },
    todaysJobs: 'Bugünün İşleri',
    noJobs: 'Bugün vadesi gelen iş yok.',
    job: 'İş',
    due: 'Vade',
    status: 'Durum',
    viewPlan: 'Planı aç',
    fleetTitle: 'PMS Uyumu',
    vessel: 'Gemi',
    compliance: 'PMS Uyumu',
    overdue: 'Gecikmiş',
    criticalOverdue: 'Kritik Gecikme',
    openDefects: 'Açık arıza',
    breakdowns: 'Breakdown',
    dataAsOf: 'Verinin tarihi',
    complianceHint:
      'Son 30 günde vadesi gelen işlerden gecikmiş kalmayanların oranı (BIMCO PI065). Onaylı erteleme gecikme sayılmaz.',
    noData: 'PMS işi yok',
    setup: 'Kurulum',
    empty: 'Henüz PMS verisi olan gemi yok.',
    open: '{{name}} ekipman listesini aç',
  },
  bell: {
    title: 'Gecikmiş kritik işler',
    item: '{{code}} · {{days}} gün gecikti',
    itemNoDays: '{{code}} · gecikti',
    workOrderOverdue: 'Hedef tarihi geçmiş iş emri',
    workOrderOverdueItem: '{{wo}} · hedef tarihten {{days}} gün geçti',
    cmReportPending: 'Durum izleme raporu bekleniyor',
    cmReportPendingItem: '{{wo}} · servis sağlayıcının raporu',
  },
  settings: {
    title: 'Planlı Bakım (PMS)',
    intro:
      'Uyarı ve rapor eşikleri. Uyum kuralları (gelecek tarih yok, düşen çalışma saati yok, günde en çok 24 saat, açık onay adımı) her zaman uygulanır ve ayar değildir [NR496 1.4.2].',
    dueSoonDays: '"Due Soon" penceresi',
    dueSoonHint: 'İş, vadesine bu kadar gün kala "Due Soon" görünür.',
    lateEntryDays: 'Geç giriş eşiği',
    lateEntryHint: 'İşten bu kadar gün sonra girilen kayıtlar "Late Entries" raporunda listelenir.',
    certificateExpiringDays: 'Sertifika "Expiring" eşiği',
    certificateHint:
      'Gemi sertifikası bitişine bu kadar gün kala "Expiring" görünür. Hatırlatmalar 90, 60 ve 30 günde gider.',
    days: 'gün',
    save: 'Kaydet',
    saved: 'Kaydedildi.',
    readOnly: 'Şirket ayarları ofiste değiştirilir.',
    structure: 'Varsayılan ekipman yapısı',
    structureHint:
      'Henüz ekipmanı olmayan gemiye standart sistemleri yükler (PMS belgesi §1, §17).',
    structureVessel: 'Gemi',
    structureNone: 'Bütün gemilerin ekipmanı var ya da kurulumdaki gemi yok.',
    structureLoaded: '{{count}} öğe eklendi.',
    load: 'Varsayılan yapıyı yükle',
  },
};
